/**
 * Utility functions for time parsing and formatting
 */

export interface ParsedTime {
  hour?: number;
  minute?: number;
  period?: string;
}

/**
 * Parse time input string with enhanced handling of formats
 */
export function parseTimeInput(input: string, startTimeValue?: string): ParsedTime {
  if (!input) return {};

  // Remove all spaces and convert to lowercase
  input = input.replace(/\s+/g, '').toLowerCase();

  const result: ParsedTime = {};

  // Handle relative time formats like "30m", "1h", "1.5h"
  const relativeMatch = input.match(/^(\d+(\.\d+)?)([hm])$/);
  if (relativeMatch && startTimeValue) {
    const [_, value, __, unit] = relativeMatch;
    const numValue = parseFloat(value);

    // Calculate relative to start time
    const startParsed = parseTimeInput(startTimeValue);
    if (startParsed.hour !== undefined) {
      let minutesToAdd = 0;
      if (unit === 'h') {
        minutesToAdd = Math.round(numValue * 60);
      } else {
        // unit === 'm'
        minutesToAdd = Math.round(numValue);
      }

      const startTotalMinutes = startParsed.hour * 60 + (startParsed.minute || 0);
      const endTotalMinutes = startTotalMinutes + minutesToAdd;

      result.hour = Math.floor(endTotalMinutes / 60) % 24;
      result.minute = endTotalMinutes % 60;
      return result;
    }
  }

  // Check for A/P abbreviations first
  if (input.endsWith('a')) {
    result.period = 'am';
    input = input.slice(0, -1);
  } else if (input.endsWith('p')) {
    result.period = 'pm';
    input = input.slice(0, -1);
  }
  // Then check for AM/PM full format
  else if (input.includes('am')) {
    result.period = 'am';
    input = input.replace('am', '');
  } else if (input.includes('pm')) {
    result.period = 'pm';
    input = input.replace('pm', '');
  }

  // Handle various time formats:

  // Format: Single digit (9) or double digit (11)
  if (/^\d{1,2}$/.test(input)) {
    result.hour = parseInt(input);
    result.minute = 0;

    // Default 1-6 to PM
    if (!result.period && result.hour >= 1 && result.hour <= 6) {
      result.period = 'pm';
    }
  }

  // Format: 3 digits without colon (630 -> 6:30, 113 -> 11:30)
  else if (/^\d{3}$/.test(input)) {
    // Smart parsing for 3 digits
    // If first two make valid hours (10-12) and last digit is 0-5, parse as hh:m0
    // Otherwise, use the old logic
    const firstTwo = parseInt(input.slice(0, 2));
    const lastDigit = parseInt(input.slice(2));

    if (firstTwo >= 10 && firstTwo <= 12 && lastDigit >= 0 && lastDigit <= 5) {
      // Interpret as 11:30 (not 1:13)
      result.hour = firstTwo;
      result.minute = lastDigit * 10;
    } else {
      // Default interpretation: 1:13
      result.hour = parseInt(input.slice(0, 1));
      result.minute = parseInt(input.slice(1));
    }

    // Default 1-6 to PM
    if (!result.period && result.hour >= 1 && result.hour <= 6) {
      result.period = 'pm';
    }
  }

  // Format: 4 digits without colon (1830 -> 18:30)
  else if (/^\d{4}$/.test(input)) {
    result.hour = parseInt(input.slice(0, 2));
    result.minute = parseInt(input.slice(2));
  }

  // Format: time with colon (9:30, 14:45)
  else if (/^\d+:\d+$/.test(input)) {
    const [hourStr, minuteStr] = input.split(':');
    result.hour = parseInt(hourStr);
    result.minute = parseInt(minuteStr);

    // Assume PM for 1-6 without AM/PM specified
    if (!result.period && result.hour >= 1 && result.hour <= 6) {
      result.period = 'pm';
    }
  }

  // Format: Partial minutes (11:3 -> 11:30)
  else if (/^\d+:\d$/.test(input)) {
    const [hourStr, minuteStr] = input.split(':');
    result.hour = parseInt(hourStr);
    result.minute = parseInt(minuteStr) * 10; // Convert single digit to tens (3 -> 30)

    // Assume PM for 1-6 without AM/PM specified
    if (!result.period && result.hour >= 1 && result.hour <= 6) {
      result.period = 'pm';
    }
  }

  // If no patterns matched but some parsing was done
  else if (input.includes(':')) {
    const [hourStr, minuteStr] = input.split(':');
    if (hourStr) result.hour = parseInt(hourStr);
    if (minuteStr) result.minute = parseInt(minuteStr);
  } else {
    // Last attempt to parse as an integer
    const hour = parseInt(input);
    if (!isNaN(hour)) {
      result.hour = hour;
      result.minute = 0;
    }
  }

  // Handle 12-hour conversion if period is set
  if (result.period === 'pm' && result.hour !== undefined && result.hour < 12) {
    result.hour += 12;
  } else if (result.period === 'am' && result.hour === 12) {
    result.hour = 0;
  }

  return result;
}

/**
 * Format time nicely in 12-hour format with am/pm
 */
export function formatTime(parsed: ParsedTime): string {
  if (parsed.hour === undefined || parsed.hour === null) return '';

  let hour = parsed.hour;
  let period = '';

  // Convert to 12-hour format
  if (hour >= 12) {
    period = 'pm';
    hour = hour > 12 ? hour - 12 : hour;
  } else {
    period = 'am';
    hour = hour === 0 ? 12 : hour;
  }

  const minute = parsed.minute !== undefined ? parsed.minute : 0;

  return `${hour}:${minute.toString().padStart(2, '0')}${period}`;
}

/**
 * Convert time string to minutes since midnight
 */
export function convertTimeToMinutes(timeStr: string, startTimeStr?: string): number {
  if (!timeStr) return 0;

  const parsed = parseTimeInput(timeStr, startTimeStr);
  if (parsed.hour === undefined) return 0;

  return parsed.hour * 60 + (parsed.minute || 0);
}

/**
 * Calculate new end time based on start time and duration
 */
export function calculateEndTime(startTimeMinutes: number, durationMinutes: number): ParsedTime {
  // Calculate the new end time in minutes
  const endTimeMinutes = (startTimeMinutes + durationMinutes) % (24 * 60);

  // Convert back to hours and minutes
  const endHour = Math.floor(endTimeMinutes / 60) % 24;
  const endMinute = endTimeMinutes % 60;

  return {
    hour: endHour,
    minute: endMinute,
  };
}

/**
 * Calculate duration between two times in minutes
 */
export function calculateDuration(startTimeMinutes: number, endTimeMinutes: number): number {
  // Handle wrapping to next day
  return endTimeMinutes >= startTimeMinutes
    ? endTimeMinutes - startTimeMinutes
    : endTimeMinutes + 24 * 60 - startTimeMinutes;
}

/**
 * Convert time from 12-hour format (e.g., "9:00am") to 24-hour format (e.g., "09:00")
 */
export function convertTo24HourFormat(timeStr: string): string {
  const parsed = parseTimeInput(timeStr);
  if (parsed.hour === undefined) return '';

  const hour = parsed.hour.toString().padStart(2, '0');
  const minute = (parsed.minute || 0).toString().padStart(2, '0');

  return `${hour}:${minute}`;
}
